import React, { memo, ReactElement, useCallback, useMemo } from "react";
import classNames from "classnames";
import { TipBoxData } from "@swiggy-private/cart-types";
import { CloseIcon } from "@cart-ui/widgets/close-icon";
import { TIP_DETAIL_TEST_IDS } from "@swiggy-private/cart-test-ids";

import styles from "./TipBox.scss";
import { AriaHidden, format } from "@swiggy-private/core-ui";
import { UX4_PRIMARY_ORANGE } from "@cart-ui/constants/Color";

const MOST_TIPPED_TEXT = "Most Tipped";
const TIP_RUPEE = "Tip Rupee";
const SELECTED_TEXT = "is selected";
const AMOUNT_BOX_ID = "AMOUNT_BOX_ID_{0}";

const TipBox = ({
    selected,
    showMostTippedText,
    text,
    boxAmount,
    showRsIcon,
    showCloseIcon,
    onTipBoxSelection,
}: TipBoxData): ReactElement => {
    const containerClass = classNames(styles.tipBoxContainer, {
        [styles.tipBoxSelected]: selected,
        [styles.tipBoxContainerMostTipped]: showMostTippedText,
    });

    const textClass = classNames({ [styles.tipBoxTextRupee]: showRsIcon });
    const textContainerClass = classNames(styles.tipBoxText, {
        [styles.tipBoxTextMostTipped]: showMostTippedText,
    });

    const accessibilityId = useMemo(() => format(AMOUNT_BOX_ID, text), [text]);

    const onClickCallBack = useCallback(() => {
        typeof onTipBoxSelection === "function" && onTipBoxSelection({ amount: boxAmount, selected: !selected });
        document?.getElementById(accessibilityId)?.focus();
    }, [onTipBoxSelection, boxAmount, selected, accessibilityId]);

    const tipBoxAriaLabel = useMemo(
        () => `${TIP_RUPEE} ${text} ${showMostTippedText ? MOST_TIPPED_TEXT : ""} ${selected ? SELECTED_TEXT : ""}`,
        [selected, showMostTippedText, text],
    );

    return (
        <button
            className={containerClass}
            onMouseDown={onClickCallBack}
            aria-label={tipBoxAriaLabel}
            id={accessibilityId}
            data-testid={TIP_DETAIL_TEST_IDS.DE_TIP_AMOUNT_VALUES}
        >
            <AriaHidden as="span" className={textContainerClass}>
                <span className={textClass}>{text}</span>
                {showMostTippedText && <span className={styles.tipBoxMostTippedContainer}>{MOST_TIPPED_TEXT}</span>}
            </AriaHidden>
            {showCloseIcon && (
                <span className={styles.tipBoxCloseContainer}>
                    <CloseIcon IconBgColor={UX4_PRIMARY_ORANGE} />
                </span>
            )}
        </button>
    );
};

export default memo(TipBox);
